import { BaseListViewStore, BooleanToggleCellData, checkPermissions } from 'core/stores';
import { action, runInAction } from 'mobx';
import { ConfirmModalParams, ModalParams } from 'core/models';
import { ApplicationStore } from 'platform/application/stores';
import { observable, makeObservable } from 'mobx';
import { notificationError, isSuperAdminOrganization } from 'core/utils';
import { SellerOrganizationInfoStore } from 'modules/seller/stores';
import { sourceOfChange } from 'common/constants';
import _ from 'lodash';

class SellerListViewStore extends BaseListViewStore {
	@observable applicationSort = [{ field: 'applicationTitle', dir: 'asc' }];
	application;

	activationConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.changeOrganizationActive(this.application, !this.application.isActive);
		},
	});

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ApplicationStore(rootStore),
			findFunction: 'findSellerOrganization',
			queryParams: {
				embed: ['sellerOrganizationInfo'],
			},
			tableData: {
				columns: [
					{
						key: 'applicationTitle',
						title: 'SELLERVIEW.APPLICATIONTITLE',
						selected: true,
						width: 200,
						sortable: true,
					},
					{
						key: 'identifier',
						title: 'SELLERVIEW.IDENTIFIER',
						selected: true,
						width: 200,
						sortable: true,
					},
					{
						key: 'sellerOrganizationInfo.printPackingSlip',
						title: 'SELLERVIEW.PRINTPACKINGSLIP',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (seller, field) => this.printPackingSlipToggle(seller, field),
						isClickable: true,
					},
					{
						key: 'sellerOrganizationInfo.partiallyFulfillMixedOrders',
						title: 'SELLERVIEW.SPLITORDERSWITHNONACTIVESKU',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (seller, field) => this.partiallyFulfillMixedOrdersToggle(seller, field),
						isClickable: true,
					},
				],
				sortObject: [],
				actions: {
					onEdit: application => this.routes.edit(application.id),
					onSort: column => this.queryUtility.changeOrder(column.key),
					onChangeOrganizationActive: application => this.openConfirmModal(application, !application.isActive),
					onEditSeller3PLCredentials: async application => this.openEditSeller3PLCredentialsModal(application),
					onEditSellerWarehouses: async application => this.openEditSellerWarehousesModal(application),
				},
			},
			routes: {
				create: () => this.rootStore.routerStore.goTo('master.app.main.seller.create'),
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.seller.edit', {
						id,
					}),
			},
			permissions: {
				update: () => this.authStore.hasPermission(''),
				create: () => this.authStore.hasPermission(''),
				remove: () => this.authStore.hasPermission(''),
				read: () => this.authStore.hasPermission(''),
				full: () => this.authStore.hasPermission(''),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(this.rootStore)) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.sellerOrganizationInfoStore = new SellerOrganizationInfoStore(rootStore);
		this.queryUtility.initialize(this.findSellerOrganization);
		this.showActions = checkPermissions(this).actionsAllowed;
		this.setButtons();
		this.setSearchVisible();
	}

	@action.bound async findSellerOrganization() {
		this.suspend();
		runInAction(() => {
			//Default sorting
			this.queryUtility.filter.sellerOnly = true;
		});

		await this.listStore.find(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.resume();
	}

	@action.bound setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'platformSection.create',
					text: 'SELLERVIEW.NEWSELLER',
				},
			],
		});
	}

	@action.bound openConfirmModal(application, isReactivate) {
		this.activationConfirmModal.open({
			message: [
				isReactivate ? 'ORGANIZATION.LIST.REACTIVATECONFIRM' : 'ORGANIZATION.LIST.DEACTIVATECONFIRM',
				{ name: application.identifier },
			],
		});
		this.application = application;
	}

	@action.bound async changeOrganizationActive(application, isReactivate) {
		this.suspend();
		try {
			await this.listStore.changeStatus(application.id, isReactivate);
			this.rootStore.notificationStore.success([
				isReactivate ? 'ORGANIZATION.LIST.REACTIVATESUCCESS' : 'ORGANIZATION.LIST.DEACTIVATESUCCESS',
				{ name: application.identifier },
			]);
			this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	openEditSeller3PLCredentialsModal = item => {
		this.selectedItem = item;
		this.editSeller3PLCredentialsModal.open();
	};
	editSeller3PLCredentialsModal = new ModalParams();

	closeEditSeller3PLCredentialsModal = () => {
		this.editSeller3PLCredentialsModal.close();
	};

	openEditSellerWarehousesModal = item => {
		this.selectedItem = item;
		this.editSellerWarehousesModal.open();
	};

	closeEditSellerWarehousesModal = () => {
		this.editSellerWarehousesModal.close();
	};

	editSellerWarehousesModal = new ModalParams();

	@action.bound
	async printPackingSlipToggle(seller, field) {
		this.suspend();
		try {
			const sellerOrganizationInfo = _.get(seller, field.split('.')[0]);
			sellerOrganizationInfo.printPackingSlip = !sellerOrganizationInfo.printPackingSlip;
			await this.sellerOrganizationInfoStore.updateSellerOrganizationInfo(
				sellerOrganizationInfo,
				sourceOfChange.printPackingSlipToggleSellerList
			);
			this.rootStore.notificationStore.success(['LOOKUPUPDATE.SELLER', { seller: seller.applicationTitle }]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	@action.bound
	partiallyFulfillMixedOrdersToggle = async (seller, field) => {
		this.suspend();
		try {
			const sellerOrganizationInfo = _.get(seller, field.split('.')[0]);
			sellerOrganizationInfo.partiallyFulfillMixedOrders = !sellerOrganizationInfo.partiallyFulfillMixedOrders;
			await this.sellerOrganizationInfoStore.updateSellerOrganizationInfo(
				sellerOrganizationInfo,
				sourceOfChange.partiallyFulfillMixedOrdersToggleSellerList
			);
			this.rootStore.notificationStore.success(['LOOKUPUPDATE.SELLER', { seller: seller.applicationTitle }]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};
}

export default SellerListViewStore;



// WEBPACK FOOTER //
// ./src/modules/seller/stores/SellerListViewStore.js